// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Double linked list implementation.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { DecoratedError } from "../logging/index.js"

// @@body
/**
 * Callback returning a boolean if the node parameter meets match criteria.
 * @template NodeType
 * @callback findLinkedListNodeCallback
 * @param {DoubleLinkedListNode.<NodeType>} node Current node to test for
 * against match criteria implemented in callback.
 * @returns {boolean} Does node pass callback match test?
 */

/**
 * Double linked list implementation providing:
 * - Methods to add/remove nodes.
 * - Methods to find/traverse nodes.
 * - Access to all node *values* in a flat array.
 * @template NodeType
 */
class DoubleLinkedList {
    #head = /** @type {?DoubleLinkedListNode.<NodeType>} */ (null)
    #tail = /** @type {?DoubleLinkedListNode.<NodeType>} */ (null)
    #array = /** @type {Array.<DoubleLinkedListNode.<NodeType>>} */ ([])
    #releasedNode = /** @type {?DoubleLinkedListNode.<NodeType>} */ (null)

    /**
     * Construct linked list from an initial array of node *values*.
     * @param {Array.<DoubleLinkedListNode.<NodeType>|NodeType>} [data] Array of
     * initial node *values* to be or node *instances* to be added to the
     * constructed linked list. Note that:
     * - Node values will be converted to new linked list nodes.
     * - Node instances will be added directly to the linked list, and *must
     * not* be an existing member of another linked list.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.isClosed] Should new linked list be circular?
     */
    constructor(data, { isClosed = false } = {}) {
        if (data) {
            // Add node to list for each value in data array depending on if
            // value is node *instance* or raw *value*.
            for (const value of data) {
                if (value instanceof DoubleLinkedListNode) {
                    this.addNode(value)
                }
                else {
                    const node = new DoubleLinkedListNode(value)
                    this.addNode(node)
                }
            }

            // Optionally close linked list making a *circular* list, but only
            // if node data values have also been provided, else list cannot be
            // closed.
            if (isClosed) { this.close() }
        }
    }

    /**
     * Add node to current linked list instance. Node will be added to the end
     * of the linked list by default, but depending on the parent node reference
     * passed, may be added at any point in the list.
     * @param {DoubleLinkedListNode.<NodeType>} newNode New node to be added
     * to linked list.
     * @param {object} obj Optional destructured parameters object.
     * @param {?DoubleLinkedListNode.<NodeType>|number} [obj.parentNode]
     * Parent node (or reference to parent node) for new node. New node will be
     * added *after* this node (i.e. the `next` property of the parent node will
     * point to the new node, and the `previous` property of the new node will
     * point to the parent node). If `null`, new node will *replace* the `head`
     * node, if `number`, parent node will be found by index, and if not given,
     * new node will be appended to the end of the list and replace the `tail`
     * node.
     * @throws {DoubleLinkedListError} Throws when:
     * - Node already exists in this list.
     * - Node already exists in another list.
     * - Index reference of parent node is out of bounds of internal node array.
     * - Parent node is not found in internal node array.
     */
    addNode(newNode, { parentNode = this.#tail } = {}) {
        // Throw if new node is not contained in linked list.
        if (newNode.list) {
            throw newNode.list === this
                ? new DoubleLinkedListError({
                    message: "New node already exists in this list",
                    fix: "Do not add nodes that are already in list"
                })
                : new DoubleLinkedListError({
                    message: "New node already in another list",
                    fix: "Do not add nodes that are already in another list"
                })
        }

        // Fetch parent node by index reference, or check parent node exists
        // in linked list if parent node passed as node. Throw if parent node
        // does not resolve to a node contained in the linked list.
        let index = - 1
        if (typeof parentNode === "number") {
            const linkedNode = this.nodeAtIndex(parentNode)
            if (!linkedNode) {
                throw new DoubleLinkedListError({
                    message: "Parent node not found at index",
                    fix: "Supply index in range of list size"
                })
            }
            index = parentNode
            parentNode = linkedNode
        }
        else if (parentNode) {
            index = this.#array.indexOf(parentNode)
            if (parentNode.list !== this || index === - 1) {
                throw new DoubleLinkedListError({
                    message: "Parent node not found in list",
                    fix: "Supply a parent node which exists in list"
                })
            }
        }

        // Add new node, updating head node or parent next node as required
        // depending on insertion point.
        newNode.list = this
        if (!parentNode) { this.#updateHeadNode(newNode) }
        else { this.#updateNextNode(parentNode, newNode) }

        // Update tail node.
        if (parentNode === this.#tail) { this.#tail = newNode }

        // Update internal node array.
        this.#array.splice(index + 1, 0, newNode)
    }

    /**
     * Add multiple new nodes to current linked list instance *in sequence*
     * following a specified parent node. If no parent node is specified, all
     * new nodes will be added to the end of the list.
     * @param {Array.<DoubleLinkedListNode.<NodeType>>} newNodes Array of new
     * nodes to sequentially add to linked list.
     * @param {object} obj Optional destructured parameters object.
     * @param {?DoubleLinkedListNode.<NodeType>|number} [obj.parentNode]
     * Parent node, or reference to parent node, for new node. See
     * {@link DoubleLinkedList.addNode addNode} parentNode parameter for more
     * information.
     * @see {@link DoubleLinkedList#addNode}
     */
    addNodes(newNodes, { parentNode = this.#tail } = {}) {
        // Add nodes sequentially, updating parent node each cycle.
        for (const newNode of newNodes) {
            this.addNode(newNode, { parentNode })
            parentNode = newNode
        }
    }

    /**
     * Remove a node from the current linked list instance. Optionally remove
     * all `child` nodes (nodes on `next` property of old node) up to and
     * including the `tail` node if recursive mode specified.
     * @param {DoubleLinkedListNode.<NodeType>|number} oldNode Old node (or
     * reference to old node) which should be removed. If `number`, old node
     * will be found by index.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.recursive] Should recursively remove *all* nodes
     * until the `tail` node of the list?
     * @param {boolean} [obj.hard] Should hard reset `next` and `previous`
     * reference properties on removed node after removal from list?
     * @returns {void} Returns call to removeNodes method if recursive mode
     * enabled.
     * @throws {DoubleLinkedListError} Throws when:
     * - Index reference of old node is out of bounds of internal node array.
     * - Old node is not found in internal node array.
     */
    removeNode(oldNode, { recursive = false, hard = false } = {}) {
        // Fetch old node by index reference, or check old node exists in linked
        // list if old node passed as node. Throw if old node does not resolve
        // to a node contained in the linked list.
        let index
        if (typeof oldNode === "number") {
            const linkedNode = this.nodeAtIndex(oldNode)
            if (!linkedNode) {
                throw new DoubleLinkedListError({
                    message: "Node to be removed not found at index",
                    fix: "Supply index in range of list size"
                })
            }
            index = oldNode
            oldNode = linkedNode
        }
        else {
            index = this.#array.indexOf(oldNode)
            if (index === - 1) {
                throw new DoubleLinkedListError({
                    message: "Node to be removed not found in list",
                    fix: "Supply a node which exists in list"
                })
            }
        }

        // Remove all child nodes of old node *first* if recursive mode set.
        if (recursive) {
            const oldNodes = this.#array.slice(index)
            return this.removeNodes(oldNodes)
        }

        // Remove node.
        this.#removeNode(oldNode, hard)

        // Update internal node array.
        this.#array.splice(index, 1)
    }

    /**
     * Remove all nodes passed in array to method from current linked list
     * instance. Nodes or references to nodes (indexes) may be passed.
     * @param {Array.<DoubleLinkedListNode.<NodeType>|number>} oldNodes Array
     * of old nodes (or references to old nodes) which should be removed. See
     * {@link DoubleLinkedList.removeNode removeNode} oldNode parameter for more
     * information.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.safe] Should nodes be removed in safe mode (i.e.)
     * if a node is not found at the given index, or if a node does not exist in
     * the list it should be ignored rather than throwing an error.
     * @param {boolean} [obj.hard] Should hard reset `next` and `previous`
     * reference properties on removed nodes after removal from list?
     * @see {@link DoubleLinkedList#removeNode}
     */
    removeNodes(oldNodes, { safe = true, hard = false } = {}) {
        // Sanitize old node array.
        for (let i = 0; i < oldNodes.length; i++) {
            const oldNodeOrIndex = oldNodes[i]

            // Convert all node index references to nodes such that index
            // references are not out of sync in the removal loop (i.e. index
            // `x` will not always refer to the *same* node as other nodes are
            // removed from the list).
            if (typeof oldNodeOrIndex === "number") {
                const oldNode = this.nodeAtIndex(oldNodeOrIndex)
                if (oldNode) { oldNodes[i] = oldNode }

                // Remove index reference from array if safe mode enabled and
                // node not found at index. Otherwise set to `Infinity` which
                // will *throw* in `removeNode` method.
                else { safe ? oldNodes.splice(i, 1) : oldNodes[i] = Infinity }
            }

            // Remove node from array if safe mode enabled and node not in list.
            else if (safe && !this.nodeExists(oldNodeOrIndex)) {
                oldNodes.splice(i, 1)
            }
        }

        // Remove all nodes provided.
        for (const oldNode of oldNodes) { this.removeNode(oldNode, { hard }) }
    }

    /**
     * Split current linked list instance *after* provided split node by
     * removing forward and backward links. Note that:
     * - If list is *linear*, any nodes orphaned by the split operation will be
     * returned in a new linked list.
     * - If list is *circular*, head and tail nodes will be reassigned either
     * side of the split point (split node will be new tail, and next node will
     * be new head).
     * - Method can optionally delete nodes before split.
     * @param {DoubleLinkedListNode.<NodeType>|number} splitNode Node to split
     * linked list at. Note that the list will be split *after* this node (i.e.
     * this node's `next` property will be set to `null`).
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.deletionCount] Optional deletion count for nodes
     * which should be removed *after* the split node and *before* the split
     * operation.
     * @returns {?DoubleLinkedList.<NodeType>} Optional new orphaned linked list
     * resulting from split operation. Note that:
     * - If split operation orphans any nodes, these will be inserted into a new
     * linked list which will be returned by this function.
     * - Given that *one* split operation can only orphan *one* continuously
     * linked segment of a linked list, this method will only ever need to
     * return *one* new linked list.
     * @throws {DoubleLinkedListError} Throws when:
     * - Index reference of split node is out of bounds of internal node array.
     * - Split node is not found in internal node array.
     */
    splitAtNode(splitNode, { deletionCount = 0 } = {}) {
        // Fetch split node by index reference, or check split node exists in
        // linked list. Throw if old node does not resolve to a node contained
        // in the linked list.
        let /** @type {number} */ index
        if (typeof splitNode === "number") {
            const linkedNode = this.nodeAtIndex(splitNode)
            if (!linkedNode) {
                throw new DoubleLinkedListError({
                    message: "Split node not found at index",
                    fix: "Supply index in range of list size"
                })
            }
            index = splitNode
            splitNode = linkedNode
        }
        else {
            index = this.#array.indexOf(splitNode)
            if (index === - 1) {
                throw new DoubleLinkedListError({
                    message: "Split node not found in list",
                    fix: "Supply a split node which exists in list"
                })
            }
        }

        // Remove requested number of nodes *after* split node, and return
        // linked list if it now contains no nodes.
        this.removeNodes(Array.from({ length: deletionCount }, (_, i) => {
            return index + 1 + i
        }))
        if (!this.size) { return null }

        // Split linked list by removing links in both directions to/from the
        // split node. Additionally set split node as tail node of current
        // instance.
        let nextNode = splitNode.next
        if (nextNode === this.#tail) { this.#tail = splitNode }

        // Remove forward link.
        this.#releasedNode = splitNode
        splitNode.next = null

        // Remove backward link if present.
        this.#releasedNode = nextNode
        if (nextNode) { nextNode.previous = null }

        this.#releasedNode = null

        // If list is *linear*, and split has orphaned a segment of the linked
        // list, move orphaned nodes into a new linked list instance and return
        // result.
        if (nextNode && !nextNode.canReach(splitNode)) {
            // Reset tail node of current instance.
            this.#tail = splitNode

            /** @type {DoubleLinkedList.<NodeType>} */
            const linkedList = new DoubleLinkedList()

            // Remove each node from the current linked list instance, and add
            // it to the new linked list instance.
            while (nextNode) {
                // Fetch next node before moving to new list.
                const node = nextNode
                nextNode = nextNode.next

                // Move current node to new list.
                this.removeNode(node, { hard: true })
                linkedList.addNode(node)
            }

            return linkedList
        }

        // If list is *circular*, update head and tail nodes and order of
        // internal node array to match split point.
        if (nextNode) {
            this.#head = nextNode
            this.#tail = splitNode
            const tailNodes = this.#array.splice(0, index + 1)
            this.#array.push(...tailNodes)
        }

        return null
    }

    /**
     * Get node at given index in the internal node array.
     * @param {number} index Index of requested node.
     * @returns {DoubleLinkedListNode.<NodeType>|undefined} Node at index, or
     * undefined if the index is out of bounds of internal node array.
     */
    nodeAtIndex(index) {
        return this.#array.at(index)
    }

    /**
     * Get node with a given node `value` property.
     * @param {NodeType} value Node with requested `value` property.
     * @returns {DoubleLinkedListNode.<NodeType>|undefined} Node with requested
     * `value` property, or undefined if no node exists with a matching `value`
     * property.
     */
    nodeWithValue(value) {
        // Fetch node with corresponding value.
        let outerNode
        for (const innerNode of this) {
            if (innerNode.value === value) {
                outerNode = innerNode
                break
            }
        }

        return outerNode
    }

    /**
     * Find *first* node in current linked list instance passing match criteria
     * implemented by callback.
     * @param {findLinkedListNodeCallback.<NodeType>} isMatch Callback function
     * returning boolean reflecting if current node in traversal matches
     * criteria. Method will return first node which returns `true` from this
     * callback.
     * @returns {DoubleLinkedListNode.<NodeType>|undefined} First node in linked
     * list passing callback function match test. Undefined if no matching node
     * found.
     */
    findNode(isMatch) {
        // Fetch node matching criteria implemented in callback function.
        let outerNode
        for (const innerNode of this) {
            if (isMatch(innerNode)) {
                outerNode = innerNode
                break
            }
        }

        return outerNode
    }

    /**
     * Get index of a given node in the internal node array.
     * @param {DoubleLinkedListNode.<NodeType>} node Node to get index of.
     * @returns {number} Index of node, `- 1` if the node is not found in the
     * internal node array.
     */
    indexOfNode(node) {
        return this.#array.indexOf(node)
    }

    /**
     * Get index of node with a given value in the internal node array.
     * @param {NodeType} value Node `value` property of node to get index of.
     * @returns {number} Index of node, `- 1` if a node with a matching `value`
     * property is not found.
     */
    indexOfValue(value) {
        return this.array.indexOf(value)
    }

    /**
     * Check if given node exists in linked list.
     * @param {DoubleLinkedListNode.<NodeType>} node Node to check.
     * @returns {boolean} Does node exist in linked list?
     */
    nodeExists(node) {
        return node.list === this && this.#array.includes(node)
    }

    /**
     * Reverse current linked list instance. After calling this method, note
     * that:
     * - Head and tail nodes will be swapped.
     * - Node `next` and `previous` reference properties will be swapped
     * for *every* node in list.
     */
    reverse() {
        // Swap head and tail nodes.
        const { head, tail } = this
        this.#head = tail
        this.#tail = head

        // Swap `next` and `previous` properties on each node in list.
        this.#array.reverse()
        for (const node of this.#array) {
            this.#releasedNode = node
            const { next, previous } = node
            node.next = previous
            node.previous = next
        }

        this.#releasedNode = null
    }

    /**
     * Open current linked list instance *if* the `isClosed` property is `true`
     * (i.e. if the list is closed). Opening the list will convert a *circular*
     * linked list into a *linear* linked list by:
     * - Setting the `head` node `previous` reference property to `null`.
     * - Setting the `tail` node `next` reference property to `null`.
     */
    open() {
        // Open *circular* linked list by updating `head` and `tail` node
        // pointers.
        if (this.isClosed && this.#head && this.#tail) {
            this.#releasedNode = this.#tail
            this.#tail.next = null
            this.#releasedNode = this.#head
            this.#head.previous = null
        }

        this.#releasedNode = null
    }

    /**
     * Close current linked list instance *if* the `isClosed` property is
     * `false` (i.e. if the list is open). Closing the list will convert
     * a *linear* linked list into a *circular* linked list by:
     * - Setting the `head` node `previous` reference property to point to the
     * `tail` node.
     * - Setting the `tail` node `next` reference property to point to the
     * `head` node.
     */
    close() {
        // Close *linear* linked list by updating `head` and `tail` node
        // pointers.
        if (!this.isClosed && this.#head && this.#tail) {
            this.#releasedNode = this.#tail
            this.#tail.next = this.head
            this.#releasedNode = this.#head
            this.#head.previous = this.tail
        }

        this.#releasedNode = null
    }

    /**
     * Generator which traverses current linked list instance between a start
     * node and an optional end node.
     * @param {DoubleLinkedListNode.<NodeType>|number} startNode Start node
     * (or index reference to start node) of linked list traversal. If `number`,
     * start node will be found by index from internal node array.
     * @param {object} obj Optional destructured parameters object.
     * @param {?DoubleLinkedListNode.<NodeType>|number} [obj.endNode] Optional
     * end node (or index reference to end node) of linked list traversal. If
     * `undefined`, end node will default to the `tail` node of the linked list
     * If `number`, end node will be found by index from internal node array.
     * @param {number} [obj.maxNodes] Maximum number of nodes to include in
     * traversal. Will override end node if maximum nodes is reached before the
     * end node is reached.
     * @param {boolean} [obj.inclusive] Should include end node in linked list
     * traversal. Defaults to `true`.
     * @throws {DoubleLinkedListError} Throws when:
     * - Specified start or end nodes are not found by reference.
     * - Specified start or end nodes are contained in the current linked list
     * instance.
     * @yields {DoubleLinkedListNode.<NodeType>} Traversed linked list nodes.
     */
    * traverse(startNode, {
        endNode = null,
        maxNodes = Infinity,
        inclusive = true
    } = {}) {
        // Fetch start node by index reference, or check start node exists
        // in linked list if start node passed as node. Throw if start node
        // does not resolve to a node contained in the linked list.
        if (typeof startNode === "number") {
            const node = this.nodeAtIndex(startNode)
            if (!node) {
                throw new DoubleLinkedListError({
                    message: "Start node not found at index",
                    fix: "Supply index in range of list size"
                })
            }
            startNode = node
        }
        else if (!this.nodeExists(startNode)) {
            throw new DoubleLinkedListError({
                message: "Start node is not contained in linked list",
                fix: "Supply a start node which is contained in linked list"
            })
        }

        // Fetch end node by index reference, or check end node exists in linked
        // list if end node passed as node. Throw if end node does not resolve
        // to a node contained in the linked list.
        if (typeof endNode === "number") {
            const node = this.nodeAtIndex(endNode)
            if (!node) {
                throw new DoubleLinkedListError({
                    message: "End node not found at index",
                    fix: "Supply index in range of list size"
                })
            }
            endNode = node
        }
        else if (endNode !== null && !this.nodeExists(endNode)) {
            throw new DoubleLinkedListError({
                message: "End node is not contained in linked list",
                fix: "Supply an end node which is contained in linked list"
            })
        }

        // 
        if (endNode === startNode) { return }

        // Yield nodes in sequence from start node to end node, finishing early
        // if maximum nodes reached.
        let count = 0
        let node = /** @type {?DoubleLinkedListNode.<NodeType>} */ (startNode)
        while (node && count < maxNodes) {
            yield node
            node = node.next
            if (node === startNode || node === endNode) {
                // Yield end node if `inclusive` mode set.
                if (inclusive && node && node === endNode) { yield node }
                node = null
            }
            count++
        }
    }

    /**
     * Iterator implementation for linked list instances. Generator function
     * yields all nodes in sequence from the `head` node to the `tail` node
     * inclusive.
     * @yields {DoubleLinkedListNode.<NodeType>} Traversed linked list nodes.
     */
    * [Symbol.iterator]() {
        // Yield nodes in sequence from `head` node to `tail` node inclusive.
        let node = /** @type {?DoubleLinkedListNode.<NodeType>} */ (this.#head)
        while (node) {
            yield node
            node = node.next
            if (node === this.#head) { node = null }
        }
    }

    /**
     * Update head node of current linked list instance. Note that this method
     * will:
     * - Change head node reference to new node.
     * - Update existing `head` node `previous` reference property to point to
     * new node.
     * - Update `tail` node as required if the linked list is *circular*.
     * @param {DoubleLinkedListNode.<NodeType>} node New head node of linked
     * list.
     */
    #updateHeadNode(node) {
        if (this.#head) {
            // Set new head node pointers based on existing head node.
            this.#releasedNode = node
            node.next = this.#head
            node.previous = this.#head.previous

            // Update head previous node as required to point forward to new
            // head node.
            if (this.#head.previous) {
                this.#releasedNode = this.#head.previous
                this.#head.previous.next = node
            }

            // Update existing head node pointers to point back to new head
            // node.
            this.#releasedNode = this.#head
            this.#head.previous = node
        }

        // Update head node.
        this.#head = node

        this.#releasedNode = null
    }

    /**
     * Update next node of a given node in the linked list. Note that this
     * method will:
     * - Insert a new node after the given parent node.
     * - Update the `next` reference property of the parent node.
     * - Update the existing next node of the parent node as required to point
     * backwards to the new next node.
     * @param {DoubleLinkedListNode.<NodeType>} parentNode Parent node for
     * insertion.
     * @param {DoubleLinkedListNode.<NodeType>} nextNode New next node of
     * given parent node.
     */
    #updateNextNode(parentNode, nextNode) {
        // Set new next node pointers based on existing parent node.
        this.#releasedNode = nextNode
        nextNode.next = parentNode.next
        nextNode.previous = parentNode

        // Update existing parent next node to point back to new parent next
        // node.
        if (parentNode.next) {
            this.#releasedNode = parentNode.next
            parentNode.next.previous = nextNode
        }

        // Update parent node to point forward to new next node.
        this.#releasedNode = parentNode
        parentNode.next = nextNode

        this.#releasedNode = null
    }

    /**
     * Remove a given node in the linked list. Note that this method will:
     * - Remove the given node from the linked list.
     * - Update the next and previous nodes of the removed nodes as required to
     * point to each other rather than the removed node.
     * - Update removed node `list` property to allow it to re-added or added to
     * a different list.
     * - *Not* reset removed node `next` and `previous` reference properties
     * (unless `hard` flag set) such that when traversing the list in a for...of
     * loop or similar, if a node is removed, the node will still point to the
     * next required node in the list for the traversal to continue correctly.
     * Note that the `next` and `previous` reference properties will be
     * overwritten anyway when re-adding the node to another list.
     * - *May* remove `next` and `previous` references if in `hard` mode.
     * @param {DoubleLinkedListNode.<NodeType>} node Node to remove from linked
     * list.
     * @param {boolean} hard Should hard reset `next` and `previous` references?
     */
    #removeNode(node, hard = false) {
        // Update `head` and `tail` nodes as required if the removed node is one
        // of these nodes.
        if (node === this.#head) { this.#head = this.#head.next }
        if (node === this.#tail) { this.#tail = this.#tail.previous }

        // Update previous and next node of removed node such that they point to
        // each other.
        if (node.previous) {
            this.#releasedNode = node.previous
            node.previous.next = node.next
        }
        if (node.next) {
            this.#releasedNode = node.next
            node.next.previous = node.previous
        }

        // Release removed node such that it can be added to another list, but
        // do *not* reset existing `next` and `previous` pointers unless hard
        // flag is set, such that removed node can still point forward and back
        // for list traversal.
        this.#releasedNode = node
        if (hard) {
            node.next = null
            node.previous = null
        }
        node.list = null

        this.#releasedNode = null
    }

    /**
     * Ordered array of node values extracted from nodes in linked list.
     */
    get array() {
        // Convert internal array to an array of node *values*.
        const array = []
        for (const node of this.#array) { array.push(node.value) }

        return array
    }

    /**
     * Number of nodes of linked list, reflecting length of internal node array.
     */
    get size() {
        return this.#array.length
    }

    /**
     * Is linked list closed (i.e. `tail` node points to `head` node making
     * a *circular* linked list)?
     */
    get isClosed() {
        return this.tail?.next === this.head
    }

    /**
     * Is linked list empty (i.e. contains no nodes)?
     */
    get isEmpty() {
        return !this.#array.length && !this.#head && !this.#tail
    }

    /**
     * Head node of current linked list instance.
     */
    get head() {
        return this.#head
    }

    /**
     * Tail node of current linked list instance.
     */
    get tail() {
        return this.#tail
    }

    /**
     * Current node released for reference property editing. Note that:
     * - *Only* the node referenced by this property will allow property
     * setting; attempting to set properties on a node which is *not*
     * referenced by this `releasedNode` property will throw an error.
     * - The value of `releasedNode` will be set *as required* by any of the
     * internal *private* methods called by any public list mutation method
     * ({@link DoubleLinkedList.addNode addNode},
     * {@link DoubleLinkedList.removeNode removeNode} etc.).
     * - The value of `releasedNode` will be set back to `null` after any
     * mutation is complete.
     * @see {@link DoubleLinkedList#addNode}
     * @see {@link DoubleLinkedList#removeNode}
     */
    get releasedNode() {
        return this.#releasedNode
    }
}

/**
 * Double linked list node implementation providing:
 * - Reference properties for `next` and `previous` nodes in linked list.
 * - Reference to `list` container.
 * - Set protections on all properties such that *only* the current linked list
 * container can update node reference properties.
 * @template NodeType
 */
class DoubleLinkedListNode {
    #list = /** @type {?DoubleLinkedList.<NodeType>} */ (null)
    #next = /** @type {?DoubleLinkedListNode.<NodeType>} */ (null)
    #previous = /** @type {?DoubleLinkedListNode.<NodeType>} */ (null)

    /**
     * Construct linked list node with data value of node.
     * @param {NodeType} value Data value of node.
     */
    constructor(value) {
        this.value = value
    }

    /**
     * Check if the current linked list node instance can reach a given node
     * by traversing the linked in the forwards or backwards direction.
     * @param {DoubleLinkedListNode.<NodeType>} targeNode Node which we are
     * trying to reach in either direction from the current node instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.reverse] Should we test in reverse direction?
     * @returns {boolean} Is target node reachable from current node instance?
     */
    canReach(targeNode, { reverse = false } = {}) {
        /** @type {?DoubleLinkedListNode.<NodeType>} */
        let currentNode = this
        do {
            currentNode = reverse ? currentNode.previous : currentNode.next
            if (currentNode === targeNode) { return true }
        } while (currentNode && currentNode !== this)

        return false
    }

    /**
     * Copy current node instance with duplicate node *value* passed by
     * reference (i.e. not a copied node will not contain a deep copy of the
     * current node value).
     * @returns {DoubleLinkedListNode.<NodeType>} Copy of current node instance.
     */
    copy() {
        return new DoubleLinkedListNode(this.value)
    }

    /**
     * Is node released by linked list container for reference property editing.
     * Note that this property will return `true` by default, and will instead
     * throw a suitable error when the node is not released.
     * @returns {boolean} True by default.
     * @throws {DoubleLinkedListNodeError} Throws when:
     * - Node has no assigned linked list container.
     * - Linked list container has not released node for editing.
     */
    get #isReleased() {
        // Throw if list unassigned or if linked list container has not released
        // this node for editing.
        if (!this.#list) {
            throw new DoubleLinkedListNodeError({
                message: "Node has no assigned list",
                fix: "Ensure that node is assigned to a list before using"
            })
        }
        if (this.#list.releasedNode !== this) {
            throw new DoubleLinkedListNodeError({
                message: "Node not released for update by list",
                fix: "Do not mutate node directly, use DoubleLinkedList methods"
            })
        }

        return true
    }

    /**
     * Linked list container of node.
     */
    get list() {
        return this.#list
    }

    set list(list) {
        if (this.#list === null || this.#isReleased) { this.#list = list }
    }

    /**
     * Next node in linked list container.
     */
    get next() {
        return this.#next
    }

    set next(next) {
        if (this.#isReleased) { this.#next = next }
    }

    /**
     * Previous node in linked list container.
     */
    get previous() {
        return this.#previous
    }

    set previous(previous) {
        if (this.#isReleased) { this.#previous = previous }
    }
}

/**
 * Named linked list error.
 */
class DoubleLinkedListError extends DecoratedError {
    /**
     * Construct linked list named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "DoubleLinkedListError"
    }
}

/**
 * Named linked list node error.
 */
class DoubleLinkedListNodeError extends DecoratedError {
    /**
     * Construct linked list node named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "DoubleLinkedListNodeError"
    }
}

// @@exports
export { DoubleLinkedList, DoubleLinkedListNode }
